/**
 * Global Panel Manager
 * Ensures only one panel is open at a time and manages z-index layering
 */

class PanelManager {
  constructor() {
    this.activePanels = new Map()
    this.observers = new Set()
    this.baseZIndex = 1400 // Higher than image options menu (1300) and transform gizmo (900)
  }

  /**
   * Register a panel with the manager
   * @param {string} panelId - Unique identifier for the panel
   * @param {Function} closeCallback - Function to close the panel
   */
  registerPanel(panelId, closeCallback) {
    this.activePanels.set(panelId, { 
      closeCallback, 
      isOpen: false,
      zIndex: this.baseZIndex + this.activePanels.size
    })
    this._notifyObservers('register', panelId)
  }

  /**
   * Open a panel (closes all others)
   * @param {string} panelId - Panel to open
   * @param {Object} position - Position coordinates {x, y}
   * @param {Object} data - Additional data for the panel
   * @param {boolean} forceOpen - Force open even if already open
   */
  openPanel(panelId, position = null, data = null, forceOpen = false) {
    const panel = this.activePanels.get(panelId)
    if (!panel) return

    // If this panel is already open and not forced, close it (toggle behavior)
    if (panel.isOpen && !forceOpen) {
      this.closePanel(panelId)
      return
    }

    // Close all other panels first
    for (const [otherPanelId, otherPanel] of this.activePanels) {
      if (otherPanelId !== panelId && otherPanel.isOpen) {
        otherPanel.isOpen = false
        otherPanel.closeCallback()
        this._notifyObservers('close', otherPanelId)
      }
    }
    
    // Open the requested panel
    panel.isOpen = true
    panel.position = position
    panel.data = data
    this._notifyObservers('open', panelId, { position, data })
  }

  /**
   * Switch to a panel (always opens, closes others)
   * @param {string} panelId - Panel to switch to
   * @param {Object} position - Position coordinates {x, y}
   * @param {Object} data - Additional data for the panel
   */
  switchToPanel(panelId, position = null, data = null) {
    this.openPanel(panelId, position, data, true)
  }

  /**
   * Close a specific panel
   * @param {string} panelId - Panel to close
   */
  closePanel(panelId) {
    const panel = this.activePanels.get(panelId)
    if (panel && panel.isOpen) {
      panel.isOpen = false
      panel.closeCallback()
      this._notifyObservers('close', panelId)
    }
  }

  /**
   * Close all panels except the specified one
   * @param {string} excludePanelId - Panel to keep open
   */
  closeAllPanels(excludePanelId = null) {
    for (const [panelId, panel] of this.activePanels) {
      if (panelId !== excludePanelId && panel.isOpen) {
        this.closePanel(panelId)
      }
    }
  }

  /**
   * Check if a panel is open
   * @param {string} panelId - Panel to check
   * @returns {boolean}
   */
  isPanelOpen(panelId) {
    const panel = this.activePanels.get(panelId)
    return panel ? panel.isOpen : false
  }

  /**
   * Get the z-index for a panel
   * @param {string} panelId - Panel ID
   * @returns {number}
   */
  getPanelZIndex(panelId) {
    const panel = this.activePanels.get(panelId)
    return panel ? panel.zIndex : this.baseZIndex
  }

  /**
   * Add an observer for panel state changes
   * @param {Function} callback - Observer callback
   */
  addObserver(callback) {
    this.observers.add(callback)
    return () => this.observers.delete(callback)
  }

  /**
   * Notify all observers of state changes
   * @private
   */
  _notifyObservers(action, panelId, data = null) {
    this.observers.forEach(observer => {
      try {
        observer(action, panelId, data)
      } catch (error) {
        console.warn('PanelManager observer error:', error)
      }
    })
  }

  /**
   * Unregister a panel
   * @param {string} panelId - Panel to unregister
   */
  unregisterPanel(panelId) {
    this.closePanel(panelId)
    this.activePanels.delete(panelId)
    this._notifyObservers('unregister', panelId)
  }

  /**
   * Get stats for debugging
   * @returns {Object}
   */
  getStats() {
    return {
      totalPanels: this.activePanels.size,
      openPanels: Array.from(this.activePanels.entries())
        .filter(([, panel]) => panel.isOpen)
        .map(([id]) => id),
      baseZIndex: this.baseZIndex
    }
  }
}

// Create singleton instance
const panelManager = new PanelManager()

export default panelManager